import React, { memo, useMemo, useState } from 'react';
import { Skeleton, Spin } from 'antd';
import { AssetReviewControllerListAssetReviewsParams } from '@api/Api';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import LottieComponent from '@components/Lottie';
import AssetLinksTable from '@components/Tables/AssetLinksTable/AssetLinksTable';
import { useListAssetReviewsQuery } from '@hooks/queries/assetReview';
import { useDebounced } from '@helpers/useDebounced';
import SharedLinksTabControls from '@pages/Campaigns/CampaignItem/components/SharedLinksTab/SharedLinksTabControls';

import './SharedLinksTab.less';

type SharedLinksTabProps = {
  campaignId: string;
  canManageReviews: boolean;
  isActive: boolean;
};

export default memo(function SharedLinksTab({
  campaignId,
  canManageReviews,
  isActive
}: SharedLinksTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('createdAt:DESC');
  const debouncedSearchQuery = useDebounced(searchQuery, 250);

  const {
    data: assetReviewsData,
    isLoading: loading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useListAssetReviewsQuery({
    params: {
      searchQuery: debouncedSearchQuery || undefined,
      campaignId,
      orderBy: [
        orderBy || 'createdAt:DESC'
      ] as AssetReviewControllerListAssetReviewsParams['orderBy']
    },
    enabled: isActive
  });

  const edges = useMemo(
    () => assetReviewsData?.pages.flatMap((page) => page.edges) || [],
    [assetReviewsData]
  );

  const hasActiveFilter = !!debouncedSearchQuery;

  return (
    <div className="shared-links-tab">
      <SharedLinksTabControls
        searchQuery={searchQuery}
        orderBy={orderBy}
        onSearchChange={setSearchQuery}
        onOrderByChange={setOrderBy}
      />
      <div style={{ height: 'calc(100vh - 400px)', overflow: 'auto' }}>
        <InfiniteScroll
          loading={isFetchingNextPage ? 'more' : false}
          next={fetchNextPage}
          hasMore={hasNextPage ?? false}
          loader={
            <>
              <Skeleton.Input />
              <Skeleton.Input />
            </>
          }
        >
          {loading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              <Spin size="large" />
            </div>
          )}
          {!edges.length && hasActiveFilter && !loading && (
            <div className="empty_state empty_state--search">
              <LottieComponent className="icon" view="noSearchingResult" />
              <strong
                className="main-body-text main-body-text--bold"
                style={{ marginBottom: 8 }}
              >
                No results found
              </strong>
              <p className="copy main-body-text main-body-text--dark-gray">
                It seems we can’t find any results based on your search
              </p>
            </div>
          )}
          {!edges.length && !hasActiveFilter && !loading && (
            <div className="empty_state empty_state--search">
              <LottieComponent className="icon" view="addMediaInFolder" />
              <strong
                className="main-body-text main-body-text--bold"
                style={{ marginBottom: 8 }}
              >
                No links found
              </strong>
              <p className="copy main-body-text main-body-text--dark-gray">
                Looks like there are no links in this project yet
              </p>
            </div>
          )}
          {!!edges.length && !loading && (
            <AssetLinksTable
              linksData={edges}
              loading={loading}
              campaignId={campaignId}
              canManageReviews={canManageReviews}
            />
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
});
